// @flow

import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '@streamr/streamr-layout'
import { TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import TokenMigrationBadge from '../../TokenMigration/Badge'
import AmountDisplay from './AmountDisplay'
import Container from '~shared/Container'
import PriceGraph from '~shared/PriceGraph'
import GraphNav from '~shared/PriceGraph/Nav'
import useTranslation from '~utils/useTranslation'
import Topic from '~shared/Topic'
import DangerousHtml from '~shared/DangerousHtml'
import Link from '~shared/Link'
import Display from '~shared/Display'
import Spacer from '~shared/Spacer'
import useRealtimeDataTokenStream from '~hooks/useRealtimeDataTokenStream'
import useHistoricalDataTokenStream from '~hooks/useHistoricalDataTokenStream'
import urls from '~utils/urls'

type Props = {
    className?: string,
}

const Amounts = styled.div`
    background: #FAFAFA;
    border-radius: 8px;
    padding: 3.5rem 1rem 5.3125rem 1rem;

    @media (min-width: ${TABLET}px) {
        padding: 2rem;
        width: 216px;
        box-sizing: border-box;
    }
`

const ContentArea = styled.div`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;

    div + ${Topic.Paragraph} {
        margin-top: 3.25rem;
    }

    ${TokenMigrationBadge} {
        width: 100%;
        box-sizing: border-box;
    }

    @media (min-width: ${TABLET}px) {
        max-width: 620px;
        flex-direction: row;

        ${TokenMigrationBadge} {
            display: none;
        }

        ${Topic.Paragraph} {
            flex: 1;
            text-align: left;
        }

        div + ${Topic.Paragraph} {
            margin-top: 0;
            margin-left: 4.5rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        max-width: 732px;
    }
`

const Inner = styled.div`
    background-color: #FFFFFF;
    position: relative;
    padding-bottom: 8rem;

    @media (min-width: ${TABLET}px) {
        padding-top: 3.75rem;
        padding-bottom: 11rem;
    }

    @media (min-width: ${DESKTOP}px) {
        padding-top: 9.375rem;
        padding-bottom: 18.375rem;
    }
`

const Graph = styled.div`
    display: none;

    ${PriceGraph} {
        margin-top: 10.5rem;
    }

    ${GraphNav},
    ${TokenMigrationBadge} {
        width: 285px;
        margin: 0 auto;
        background-color: #FFFFFF;
        box-sizing: border-box;
    }

    @media (min-width: ${TABLET}px) {
        display: block;
    }

    @media (min-width: ${DESKTOP}px) {
        ${GraphNav},
        ${TokenMigrationBadge} {
            margin: 0 32px;
        }
    }
`

const UnstyledToken = ({ className }: Props) => {
    const { t } = useTranslation()
    const { DATAUSDPoint, DATAUSDVolumePoint, DATAUSDMarketCapPoint } = useRealtimeDataTokenStream()
    const {
        today,
        week,
        month,
        year,
        all,
    } = useHistoricalDataTokenStream()

    const [selectedInterval, setSelectedInterval] = useState(GraphNav.Timeframe.TODAY)

    const selectedData = () => {
        switch (selectedInterval) {
            case GraphNav.Timeframe.TODAY:
                return today
            case GraphNav.Timeframe.SEVEN_DAYS:
                return week
            case GraphNav.Timeframe.ONE_MONTH:
                return month
            case GraphNav.Timeframe.ONE_YEAR:
                return year
            case GraphNav.Timeframe.ALL:
                return all
            default:
                return today
        }
    }

    return (
        <div className={className}>
            <div>
                <Container>
                    <Topic.Title $appear hash="token" tail="1.875rem,3rem,3rem">
                        {t('top.token.headline')}
                    </Topic.Title>
                </Container>
                <Graph>
                    <Container>
                        <GraphNav
                            $appear
                            switchTimeframe={setSelectedInterval}
                            loading={!today.length}
                        />
                    </Container>
                    <Spacer as={Container} head="40px,32px,">
                        <TokenMigrationBadge $appear />
                    </Spacer>
                </Graph>
            </div>
            <Graph>
                <PriceGraph
                    points={selectedData()}
                    showTime={[GraphNav.Timeframe.TODAY, GraphNav.Timeframe.SEVEN_DAYS].includes(selectedInterval)}
                    theme={{
                        background: '#FAFAFA',
                        area: '#FFFFFF',
                    }}
                />
            </Graph>
            <Inner>
                <Container>
                    <ContentArea>
                        <div>
                            <Amounts $appear>
                                <AmountDisplay
                                    name="DATA/USD"
                                    value={Math.round(DATAUSDPoint * 10000) / 10000}
                                />
                                <AmountDisplay
                                    name={`${t('general.24hVolume')}/USD`}
                                    value={Math.round(DATAUSDVolumePoint)}
                                />
                                <AmountDisplay
                                    name={`${t('general.marketCap')}/USD`}
                                    value={Math.round(DATAUSDMarketCapPoint)}
                                />
                            </Amounts>
                            <Display tablet="none">
                                <Spacer as={TokenMigrationBadge} head="40px," $appear />
                            </Display>
                        </div>
                        <Topic.Paragraph $appear>
                            <DangerousHtml tag="span">
                                {t('top.token.description', {
                                    contractAddress: process.env.GATSBY_NEW_TOKEN_ADDRESS,
                                })}
                            </DangerousHtml>
                            <span>
                                <Button
                                    tag={Link}
                                    kind="primary"
                                    size="big"
                                    to={urls.discover.dataToken}
                                    outline
                                    noDecoration
                                    noHover
                                >
                                    {t('top.token.exploreTokenomics')}
                                </Button>
                            </span>
                        </Topic.Paragraph>
                    </ContentArea>
                </Container>
            </Inner>
        </div>
    )
}

const Token = styled(UnstyledToken)`
    color: #0C009A;
    padding: 9rem 0 0;

    ${Topic.Title} {
        text-align: center;
    }

    ${Topic.Paragraph} {
        font-size: 1rem;

        > *:last-child {
            display: block;
            margin-top: 3rem;
        }
    }

    @media (min-width: ${TABLET}px) {
        background-color: #FAFAFA;

        ${Topic.Paragraph} {
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            > *:last-child {
                margin-top: 1rem;
            }
        }
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 14.5rem 0 0;

        ${Topic.Title} {
            text-align: left;
        }

        ${Topic.Paragraph} {
            font-size: 1.125rem;
        }
    }
`

export default Token
